import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { FormControl, TextField as MuiTextField } from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';

import getColor from '../../utils/color';

const useStyles = makeStyles((theme) => ({
  root: {
    // Both label rules are opt-in now: with no prop passed they emit nothing, so
    // the label keeps MUI's own colours (`text.secondary`, and `primary.main`
    // while focused), which are already theme-aware.
    // They used to default to 'theme-secondary-silver'. That key is declared ONLY
    // in the dark palette, so in light the value resolved to undefined and the
    // whole declaration was dropped (MUI's colour showed through), while in dark
    // it resolved to a dark SURFACE colour used as text — which is why input
    // labels were nearly unreadable there.
    '& label.Mui-focused': {
      color: (props) => (
        props.focusLabelColor ? getColor(theme, props.focusLabelColor) : undefined
      ),
    },
    '& label': {
      color: (props) => (
        props.labelColor ? getColor(theme, props.labelColor) : undefined
      ),
    },
    '& .MuiInput-root': {
      backgroundColor: theme.palette.secondary.silver,
      borderRadius: '3px',
      paddingLeft: '7px',
    },
    '& .MuiInput-formControl': {
      marginTop: '20px',
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset`,
      WebkitTextFillColor: theme.palette.text.primary,
      caretColor: theme.palette.text.primary,
      transition: 'background-color 9999s ease-in-out 0s',
    },

    '& input:-webkit-autofill:hover': {
      WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset`,
    },

    '& input:-webkit-autofill:focus': {
      WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset`,
    },
  },
}));

function TextField({
  id,
  label,
  value,
  type,
  onChange,
  disabled,
  fullWidth,
  variant,
  InputProps,
  select,
  options,
  required,
  multiline,
  rows,
  placeholder,
  keyId,
  ...props
}) {
  const styles = useStyles(props);
  return (
    <div>
      <FormControl className={styles.root} fullWidth={fullWidth}>
        <MuiTextField
          required = {required}
          multiline = {multiline}
          rows = {rows}
          id={id}
          type={type}
          label={label}
          value={value}
          variant={variant}
          onChange={onChange}
          disabled={disabled}
          select={select}
          placeholder={placeholder}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            ...InputProps,
          }}
        >
        {options ? options.map((option) => (
          <MenuItem key={option[keyId]} value={option[keyId].toString()}>{option.name}</MenuItem>
        )) : '' }
        </MuiTextField>
      </FormControl>
    </div>
  );
}

TextField.defaultProps = {
  // `focusLabelColor` / `labelColor` intentionally have NO default: see the note
  // in useStyles above. Leaving them undefined is what lets MUI's theme-aware
  // label colours apply, and it also preserves the specificity that page-level
  // `.MuiFormLabel-root` overrides relied on (getColor appends `!important`).
  label: '',
  value: '',
  disabled: false,
  fullWidth: false,
  type: 'text',
  InputProps: {},
  variant: 'standard',
  select: false,
  placeholder: '',
  keyId: 'id',
};

TextField.propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  type: PropTypes.string,
  variant: PropTypes.string,
  InputProps: PropTypes.object,
  focusLabelColor: PropTypes.string,
  select: PropTypes.bool,
  options: PropTypes.array,
  placeholder: PropTypes.string,
};

export default TextField;
